import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { RolesTab } from '@/components/admin/access/RolesTab'
import { TeamsTab } from '@/components/admin/access/TeamsTab'
import { UsersTab } from '@/components/admin/access/UsersTab'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'

type Tab = 'users' | 'roles' | 'teams'

/** /admin/access — users, roles and teams (z8nrz7fxtz); each tab needs its own manage permission. */
export default function AccessPage() {
  const { t } = useTranslation()
  const canUsers = usePermission(PERMISSIONS.usersManage)
  const canRoles = usePermission(PERMISSIONS.rolesManage)
  const canTeams = usePermission(PERMISSIONS.teamsManage)
  const [params, setParams] = useSearchParams()

  const tabs: Tab[] = [...(canUsers ? (['users'] as const) : []), ...(canRoles ? (['roles'] as const) : []), ...(canTeams ? (['teams'] as const) : [])]
  const requested = params.get('tab') as Tab | null
  const tab = tabs.find((x) => x === requested) ?? tabs[0]

  return (
    <Page>
      <PageHeading description={t('access.eyebrow')} title={t('access.title')} />
      {tabs.length === 0 ? (
        <p className="text-body text-ink-muted">{t('common.forbidden')}</p>
      ) : (
        <div className="flex flex-col gap-6">
          <div role="tablist" aria-label={t('access.title')} className="flex gap-6 border-b border-line">
            {tabs.map((x) => (
              <button
                key={x}
                type="button"
                role="tab"
                aria-selected={x === tab}
                onClick={() => setParams(x === tabs[0] ? {} : { tab: x }, { replace: true })}
                className={cn(
                  '-mb-px rounded-t-sm border-b-2 pb-2.5 text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  x === tab ? 'border-primary font-semibold text-primary' : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {t(`access.tabs.${x}`)}
              </button>
            ))}
          </div>
          {tab === 'users' ? <UsersTab /> : tab === 'roles' ? <RolesTab /> : <TeamsTab />}
        </div>
      )}
    </Page>
  )
}
